import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AuthAPI, ProfileAPI, tokenStore } from "@/services/api";

const AuthContext = createContext(null);
const GUEST_KEY = "truth-scan-hub-guest";

/**
 * Once-per-user idempotent claim of the 1 unused guest credit. Fires only if
 * the local guest ledger still has a positive balance at the moment auth
 * resolves. Safe to call repeatedly — the server flag `guest_bonus_claimed`
 * is the source of truth.
 */
const tryClaimGuestBonus = async (setUser) => {
  if (typeof window === "undefined") return;
  let remaining = 0;
  try {
    const raw = window.localStorage.getItem(GUEST_KEY);
    if (raw) remaining = JSON.parse(raw)?.guestCredits ?? 0;
  } catch {
    return;
  }
  if (remaining <= 0) return;
  try {
    const data = await ProfileAPI.claimGuestBonus();
    if (data?.claimed && typeof data.credits_ledger_balance === "number") {
      setUser((prev) =>
        prev
          ? { ...prev, credits_ledger_balance: data.credits_ledger_balance }
          : prev
      );
    }
    // Always zero the local guest credit after a successful claim — the
    // signed-in account is now the canonical balance source.
    try {
      window.localStorage.setItem(
        GUEST_KEY,
        JSON.stringify({ isGuest: false, guestCredits: 0 })
      );
    } catch {
      /* storage blocked */
    }
  } catch {
    /* claim failed — leave guest ledger untouched */
  }
};

/**
 * Global auth + UI-gate state.
 *  - status: 'loading' | 'authed' | 'anon'
 *  - user: { user_id, email, name, picture, credits_ledger_balance, ... }
 *  - authModalOpen / openAuthModal / closeAuthModal — global trigger so any
 *    component can request an auth prompt overlay (e.g. ScanInterface gating
 *    EXECUTE for anonymous mobile users).
 */
export const AuthProvider = ({ children }) => {
  const [status, setStatus] = useState("loading");
  const [user, setUser] = useState(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  const bootstrap = useCallback(async () => {
    try {
      const { user: u, token } = await AuthAPI.me();
      setUser(u);
      setStatus("authed");
      if (token) tokenStore.set(token);
      // Clear any "Sign in to see your results." banner reason now that auth
      // succeeded. Pending auto-scan is consumed inside `ScanInterface`.
      try {
        window.localStorage.removeItem("cmh-auth-reason");
      } catch {
        /* storage blocked */
      }
      // Reconcile any leftover guest credit into the freshly-authed ledger.
      await tryClaimGuestBonus(setUser);
    } catch {
      tokenStore.clear();
      setUser(null);
      setStatus("anon");
    }
  }, []);

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.location.hash?.includes("session_id=")
    ) {
      // AuthCallback handles the exchange; skip the /me race.
      return;
    }
    bootstrap();
  }, [bootstrap]);

  const exchangeGoogleSession = useCallback(async (sessionId) => {
    const { user: u, token } = await AuthAPI.google(sessionId);
    if (token) tokenStore.set(token);
    setUser(u);
    setStatus("authed");
    await tryClaimGuestBonus(setUser);
    return u;
  }, []);

  const logout = useCallback(async () => {
    try {
      await AuthAPI.logout();
    } catch {
      /* best-effort */
    }
    // Purge ALL client-side caches so no previous-user data can leak into
    // the next session (scan archives, dismissed banners, mirrored token).
    tokenStore.clear();
    try {
      if (typeof window !== "undefined") {
        window.localStorage.clear();
        window.sessionStorage.clear();
      }
    } catch {
      /* storage blocked */
    }
    setUser(null);
    setStatus("anon");
    // Always hard-reload back to the cold landing — guarantees every component
    // (ScanInterface, OperatorHistoryPanel, ProfilePage) drops its local state
    // even when the user logs out from `/` itself.
    if (typeof window !== "undefined") {
      window.location.replace("/");
    }
  }, []);

  const openAuthModal = useCallback((opts) => {
    if (typeof window !== "undefined") {
      try {
        if (opts && typeof opts.reason === "string" && opts.reason.length) {
          window.localStorage.setItem("cmh-auth-reason", opts.reason);
        } else {
          window.localStorage.removeItem("cmh-auth-reason");
        }
      } catch {
        /* storage blocked */
      }
    }
    setAuthModalOpen(true);
  }, []);
  const closeAuthModal = useCallback(() => {
    if (typeof window !== "undefined") {
      try {
        window.localStorage.removeItem("cmh-auth-reason");
      } catch {
        /* storage blocked */
      }
    }
    setAuthModalOpen(false);
  }, []);

  const setCredits = useCallback((next) => {
    setUser((prev) => (prev ? { ...prev, credits_ledger_balance: next } : prev));
  }, []);

  const value = useMemo(
    () => ({
      status,
      user,
      bootstrap,
      exchangeGoogleSession,
      logout,
      authModalOpen,
      openAuthModal,
      closeAuthModal,
      setCredits,
    }),
    [
      status,
      user,
      bootstrap,
      exchangeGoogleSession,
      logout,
      authModalOpen,
      openAuthModal,
      closeAuthModal,
      setCredits,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
